/* eslint-disable react-refresh/only-export-components */
import React, { useState, useMemo } from "react";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { InputOutput } from "../../InputOutput";
import { DocumentGeneratorNode } from "./doc-generator-node";
import { DocumentGeneratorProvider } from "./context";
import { useDocumentGeneratorNode } from "./hooks";
import Variables from "@/workflow_api/components/variables";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { Button } from "@elaniintc/goes-design-system";
import { useGeneratePdfPreview } from "@/queries/document-templates/document-templates.api";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

export const DocumentGeneratorDetailsForm: React.FC = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;
  const [title, setTitle] = useState<string>(
    nodeData.displayName || "Generador de documentos",
  );
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );

  return (
    <DocumentGeneratorModal
      title={title}
      setTitle={setTitle}
      modalState={modalState}
      setModalState={setModalState}
    />
  );
};

const DocumentGeneratorModal: React.FC<{
  title: string;
  setTitle: (title: string) => void;
  modalState: "idle" | "close-warning";
  setModalState: (state: "idle" | "close-warning") => void;
}> = ({ title, setTitle, modalState, setModalState }) => {
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();

  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");
  const nodeData = node?.data as unknown as NodeData;

  const {
    selectedTemplate,
    variableName,
    isVariableNameValid,
    fileName,
    fields,
    saveNode, // Habilitar saveNode
  } = useDocumentGeneratorNode();
  const [isLoading, setIsLoading] = useState(false);
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  const { mutate: generatePdf, isPending } = useGeneratePdfPreview();

  const onSaveHandler = async () => {
    try {
      setIsLoading(true);
      await saveNode(title, manualConfirmation);
      setNodeDetailsOpen(false);
      setSelectedNodeId();
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const existFields = useMemo(() => {
    return fields.every(
      (field) =>
        field.value !== undefined && field.value !== null && field.value !== "",
    );
  }, [fields]);

  const handlePreview = () => {
    const variables = fields.reduce(
      (acc, field) => {
        const label: string = field.name as string;
        const value: string = field.value as string;

        if (typeof value === "object") {
          acc[label] = "Texto no identificado";
        } else {
          acc[label] = value;
        }

        return acc;
      },
      {} as Record<string, string>,
    );

    generatePdf({
      templateId: selectedTemplate?.id,
      variables: { variables },
    });
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={
          !!selectedTemplate &&
          !!fileName &&
          !!variableName?.base &&
          isVariableNameValid &&
          existFields &&
          isNameValid(title)
        }
        isLoading={isLoading}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSaveHandler();
        }}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "file-lines",
            title: title,
            color: "action",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            manualConfirmation,
            onManualConfirmationChange: setManualConfirmation,
            rightContent: (
              <Button
                variant="quiet"
                size="sm"
                className="text-body-loud bg-neutral-object border border-neutral-quiet hover:bg-neutral-object-accent-silent"
                onClick={handlePreview}
                isLoading={isPending}
                disabled={!existFields}
              >
                Preview
              </Button>
            ),
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          leftContentWidth="min-w-[445px]"
          centerContent={<DocumentGeneratorNode />}
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
                <InputOutput
                  nodeType={NodeTypes.DOC_GENERATOR}
                  showSteps={false}
                  nodeLabel={title}
                  variableName={variableName.base}
                  variableType="file"
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const DocumentGeneratorDetails: React.FC = () => {
  return (
    <DocumentGeneratorProvider>
      <DocumentGeneratorDetailsForm />
    </DocumentGeneratorProvider>
  );
};

// Exportaciones adicionales para la historia de Storybook
export { DocumentGeneratorNode } from "./doc-generator-node";
export { DocumentGeneratorProvider } from "./context";
export { useDocumentGeneratorNode } from "./hooks";
export type {
  DocumentTemplate,
  DocumentField,
  DocumentGeneratorData,
} from "./types";
export { documentTemplates } from "./types";
